import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1670589953882-b94c9cb380f5?w=1200&q=85";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState({ name: "", email: "", phone: "", password: "", role: "client" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const user = await register(data);
      toast.success(t("auth.createdToast"));
      navigate(user.role === "owner" ? "/espace-proprietaire" : "/espace-client");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-ivory order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-serif text-3xl font-bold text-navy">{t("auth.registerTitle")}</h1>
          <p className="mt-2 text-slate-500">{t("auth.registerSub")}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div><Label>{t("auth.fullName")}</Label><Input required value={data.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" data-testid="register-name" /></div>
            <div><Label>{t("auth.email")}</Label><Input type="email" required value={data.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11" data-testid="register-email" /></div>
            <div><Label>{t("auth.phone")}</Label><Input value={data.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-11" data-testid="register-phone" /></div>
            <div><Label>{t("auth.password")}</Label><Input type="password" required minLength={6} value={data.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 h-11" data-testid="register-password" /></div>
            <div>
              <Label>{t("auth.iam")}</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-3">
                {[["client", t("auth.roleClient")], ["owner", t("auth.roleOwner")]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => set("role", v)} data-testid={`role-${v}`}
                    className={`rounded-lg border px-4 py-3 text-sm font-medium transition-all ${data.role === v ? "border-gold bg-gold/10 text-navy" : "border-slate-200 text-slate-600"}`}>{l}</button>
                ))}
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-navy text-white h-11 rounded-lg font-semibold" data-testid="register-submit">{loading ? t("auth.creating") : t("auth.createMy")}</Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">{t("auth.haveAccount")} <Link to="/login" className="font-semibold text-gold hover:underline" data-testid="to-login">{t("auth.signInLink")}</Link></p>
        </div>
      </div>
      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={HERO} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <div className="flex justify-end"><Logo variant="light" /></div>
          <div>
            <h2 className="font-serif text-4xl font-bold text-white leading-tight">{t("auth.regHeroTitle1")}<br /><span className="text-gold">{t("auth.regHeroAccent")}</span></h2>
            <p className="mt-4 text-white/70 max-w-md">{t("auth.regHeroSub")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
